import React from 'react';
import { useRouter } from 'next/router';

import { config } from 'utils/config';
import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { getNewsroom, IPrismicNewsroom } from 'providers/prismic/newsroom';
import { getArticles, IArticle } from 'providers/prismic/articles';

import { Layout } from 'containers/layout/Layout';
import { Slices } from 'containers/slices/Slices';
import { Header } from 'components/header/Header';
import { Articles } from 'components/articles/Articles';
import { ArticlesItem } from 'components/articles/ArticlesItem';
import { Pagination } from 'components/pagination/Pagination';

import PageRender, { getStaticProps as getPageStaticProps } from '../[...slug]';
import { ArticlesRelated } from 'components/articles/ArticlesRelated';
import { SliceTypes } from 'providers/prismic/slices';
import { RichText } from 'components/rich-text/RichText';

type Props = {
  preview: boolean;
  layout: IPrismicLayout;
  newsroom: IPrismicNewsroom;
  articles: IArticle[];
  currentPage: number;
  totalPages: number;
  tag?: string;
  dateFrom?: string;
  dateTo?: string;
  searchQuery?: string;
};

function NewsroomPage({
  preview,
  layout,
  newsroom,
  articles,
  currentPage,
  totalPages,
  tag,
  dateFrom,
  dateTo,
  searchQuery,
}: Props) {
  const router = useRouter();
  const [size, setSize] = React.useState(8);
  const [margin, setMargin] = React.useState(2);

  React.useEffect(() => {
    try {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } catch (error) {
      window.scrollTo(0, 0);
    }
  }, [currentPage]);

  React.useEffect(() => {
    const mqString = '(min-width: 1280px)';
    let mql = window.matchMedia(mqString);

    updatePagination(mql);
    mql.addListener(updatePagination);

    return () => {
      mql.removeListener(updatePagination);
    };
  }, []);

  const updatePagination = (e) => {
    if (e.matches) {
      setSize(8);
      setMargin(2);
    } else {
      setSize(5);
      setMargin(1);
    }
  };

  const relatedSlice = newsroom.slices
    .filter((slice) => slice.type === SliceTypes.Related)
    .find((x) => x !== undefined) as any;

  // console.log('newsroompage', newsroom);
  // console.log('articles', articles);

  return (
    <Layout
      layout={layout}
      seo={{
        title: newsroom.seo?.title || newsroom.title,
        description: newsroom.seo?.description,
        image: newsroom.seo?.image,
      }}
      header={<Header />}
      hasFooter
      isPadded
      preview={preview}
    >
      <Articles
        title={newsroom.title}
        tagline={newsroom.tagline}
        pageIndex={currentPage}
        filterTags={newsroom.filterTags}
        display={newsroom.display}
        isSlice
        noFeatured={!!tag || !!dateFrom || !!dateTo || !!searchQuery}
        related={
          relatedSlice &&
          newsroom.display === 'col-1' && (
            <ArticlesRelated
              sections={relatedSlice.items.map((item) => ({
                title: item.title,
                text: <RichText render={item.text} />,
              }))}
              mainLinkText={relatedSlice.mainLinkText}
              mainLink={relatedSlice.mainLink}
            />
          )
        }
        onFilterChange={(tag) => {
          router.push({
            pathname: router.pathname,
            query: {
              ...(tag && { tag }),
            },
          });
        }}
      >
        {articles?.map((article) => (
          <ArticlesItem
            key={article.link.href}
            title={article.title}
            text={article.summary?.trim() ? article.summary : article.extract}
            tagline={article.tagline}
            date={article.date}
            image={newsroom.display !== 'col-1' ? article.image : null}
            link={article.link}
          />
        ))}
      </Articles>
      {totalPages > 1 && (
        <Pagination total={totalPages} current={currentPage} showSize={size} marginSize={margin} />
      )}
      <Slices slices={newsroom.slices.filter((slice) => slice.type !== SliceTypes.Related)} />
    </Layout>
  );
}

export default !config.newsroomDisabled ? NewsroomPage : PageRender;

export async function getServerSideProps(args) {
  if (config.newsroomDisabled) {
    const { props } = await getPageStaticProps({
      params: {
        slug: ['newsroom'],
      },
      ...args,
    });

    return { props };
  }

  const { query, preview = false, previewData, locale } = args;
  const lang = localeToLang(locale);

  const page = parseInt(query.page, 10) || 1;
  const resultsPerPage = page === 1 ? 19 : 21;

  const layout = await getLayout({ previewData, lang });
  const newsroom = await getNewsroom({ previewData, lang, redirects: layout?.redirects });

  const { total, items: articles } =
    (await getArticles({
      previewData,
      lang,
      redirects: layout?.redirects,
      page,
      limit: resultsPerPage,
      tagline: query.tag,
      dateAfter: query.from,
      dateBefore: query.to,
      fulltext: query.q,
    })) ?? {};

  // console.log('articles', articles);

  return {
    notFound: !newsroom,
    props: {
      preview,
      layout,
      newsroom,
      // : {
      //   title: 'Latest news',
      //   tagline: 'Newsroom',
      //   tags: ['Business', 'People', 'Charity'],
      //   display: 'col-1',
      // },
      articles: articles ?? null,
      totalPages: Math.ceil(total / 21),
      currentPage: page,
      tag: query.tag ?? null,
      dateFrom: query.from ?? null,
      dateTo: query.to ?? null,
      searchQuery: query.q ?? null,
    },
  };
}
